import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { XMarkIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { GET_LOYALTY_PROGRAMS } from '../../graphql/loyalty-queries'
import {
  CREATE_LOYALTY_PROGRAM,
  PUBLISH_LOYALTY_PROGRAM,
  RENAME_LOYALTY_PROGRAM,
  SET_LOYALTY_PROGRAM_END,
} from '../../graphql/loyalty-mutations'
import type { LoyaltyProgram } from '../../types/loyalty'
import { localDateTimeString, parseApiDate } from '../../lib/format'
import { MAX_NAME_LENGTH } from './shared'

// The four lifecycle dialogs of the Curry Me Club programme admin. They are
// shared by the list (P04) and the editor (P05), so they live in their own
// file rather than inside either page.
//
// Mutation failures are already surfaced by the global errorLink in
// src/lib/apollo.ts, which toasts every GraphQL error message — a toast.error
// here would double it. Every dialog therefore just stays open on an error so
// the entry is not lost.

/** The next full hour, local — the sensible default for "starts at". */
function nextFullHour(): Date {
  const date = new Date()
  date.setMinutes(0, 0, 0)
  date.setHours(date.getHours() + 1)
  return date
}

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------

interface DialogShellProps {
  title: string
  onClose: () => void
  busy: boolean
  actionLabel: string
  actionDisabled?: boolean
  onAction: () => void
  children: React.ReactNode
}

/**
 * Header / body / footer bands over a dimmed backdrop, the same hand-rolled
 * modal DeliverySettingsPage uses. While a save is in flight the dialog is not
 * dismissable: a close racing the response would throw the entry away.
 */
const DialogShell = ({
  title,
  onClose,
  busy,
  actionLabel,
  actionDisabled = false,
  onAction,
  children,
}: DialogShellProps) => (
  <div className="fixed inset-0 z-50 overflow-y-auto">
    <div className="flex min-h-screen items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75"
        onClick={() => {
          if (!busy) onClose()
        }}
      />
      <div className="relative w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} disabled={busy} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">{children}</div>

        <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
          <button onClick={onClose} disabled={busy} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={onAction}
            disabled={busy || actionDisabled}
            className="btn-primary disabled:opacity-50"
          >
            {busy ? 'Saving…' : actionLabel}
          </button>
        </div>
      </div>
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// New draft
// ---------------------------------------------------------------------------

/**
 * Creates the draft and opens its editor. There is no `/new` route: the API
 * needs a name to create a programme, and every later edit is a mutation on
 * the returned id.
 */
export const NewDraftDialog = ({ onClose }: { onClose: () => void }) => {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [createProgram, { loading }] = useMutation(CREATE_LOYALTY_PROGRAM, {
    refetchQueries: [GET_LOYALTY_PROGRAMS],
  })

  const handleCreate = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('A name is required.')
      return
    }
    if (trimmed.length > MAX_NAME_LENGTH) {
      setError(`At most ${MAX_NAME_LENGTH} characters.`)
      return
    }
    setError(null)
    try {
      const result = await createProgram({ variables: { input: { name: trimmed } } })
      const created: LoyaltyProgram | undefined = result.data?.createLoyaltyProgram
      if (!created) return
      toast.success('Draft created')
      onClose()
      navigate(`/loyalty/programs/${created.id}`)
    } catch {
      // errorLink already toasted; keep the dialog open.
    }
  }

  return (
    <DialogShell
      title="New draft"
      onClose={onClose}
      busy={loading}
      actionLabel="Create draft"
      onAction={() => void handleCreate()}
    >
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          autoFocus
          maxLength={MAX_NAME_LENGTH}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Curry Me Club 2026"
          className={`input-field ${error ? 'border-red-300 bg-red-50' : ''}`}
        />
        {error ? (
          <p className="mt-1 text-xs text-red-600">{error}</p>
        ) : (
          <p className="mt-1 text-xs text-gray-400">
            At most {MAX_NAME_LENGTH} characters. The rules are set in the editor that opens next.
          </p>
        )}
      </div>
    </DialogShell>
  )
}

// ---------------------------------------------------------------------------
// Publish
// ---------------------------------------------------------------------------

interface PublishDialogProps {
  program: LoyaltyProgram
  /** The programme that is live right now, if any — it is what publishing closes. */
  currentProgram: LoyaltyProgram | null
  onClose: () => void
}

/**
 * Freezes a draft and gives it a start moment. The API refuses a start in the
 * past, a ladder without a stamp rung and an overlap with another programme —
 * the two checks the operator can see beforehand are shown here.
 */
export const PublishDialog = ({ program, currentProgram, onClose }: PublishDialogProps) => {
  const [startsAt, setStartsAt] = useState(() => localDateTimeString(nextFullHour()))
  const [error, setError] = useState<string | null>(null)
  // Publishing also closes the open-ended predecessor server-side, and the
  // returned programme is only the published one — the list must be re-read
  // for the predecessor's new end date and `is_current`.
  const [publishProgram, { loading }] = useMutation(PUBLISH_LOYALTY_PROGRAM, {
    refetchQueries: [GET_LOYALTY_PROGRAMS],
  })

  const hasStampTier = program.tiers.some((tier) => tier.trigger === 'STAMP')
  // Only an OPEN-ENDED predecessor gets closed by this publish; one that
  // already carries an end date keeps it.
  const closes =
    currentProgram && currentProgram.id !== program.id && !currentProgram.ends_at
      ? currentProgram.name
      : null

  const handlePublish = async () => {
    if (!startsAt) {
      setError('Pick the moment the programme starts.')
      return
    }
    const starts = new Date(startsAt)
    if (Number.isNaN(starts.getTime())) {
      setError('Pick the moment the programme starts.')
      return
    }
    setError(null)
    try {
      await publishProgram({ variables: { id: program.id, starts_at: starts.toISOString() } })
      toast.success('Programme published')
      onClose()
    } catch {
      // errorLink already toasted; keep the dialog open.
    }
  }

  return (
    <DialogShell
      title={`Publish “${program.name}”`}
      onClose={onClose}
      busy={loading}
      actionLabel="Publish"
      actionDisabled={!hasStampTier}
      onAction={() => void handlePublish()}
    >
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Starts at <span className="text-red-500">*</span>
        </label>
        <input
          type="datetime-local"
          value={startsAt}
          onChange={(e) => setStartsAt(e.target.value)}
          className={`input-field ${error ? 'border-red-300 bg-red-50' : ''}`}
        />
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>

      <div className="space-y-1 text-xs">
        <p className="text-gray-500">
          Freezes the rules.{closes && ` Closes “${closes}” at that moment.`}
        </p>
        <p className={hasStampTier ? 'text-gray-500' : 'text-red-600'}>
          Needs at least one stamp rung.
        </p>
      </div>
    </DialogShell>
  )
}

// ---------------------------------------------------------------------------
// Rename
// ---------------------------------------------------------------------------

/** The one field a published programme may still change (and a draft too). */
export const RenameDialog = ({
  program,
  onClose,
}: {
  program: LoyaltyProgram
  onClose: () => void
}) => {
  const [name, setName] = useState(program.name)
  const [error, setError] = useState<string | null>(null)
  const [renameProgram, { loading }] = useMutation(RENAME_LOYALTY_PROGRAM)

  const handleRename = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('A name is required.')
      return
    }
    if (trimmed.length > MAX_NAME_LENGTH) {
      setError(`At most ${MAX_NAME_LENGTH} characters.`)
      return
    }
    setError(null)
    try {
      await renameProgram({ variables: { id: program.id, name: trimmed } })
      toast.success('Programme renamed')
      onClose()
    } catch {
      // errorLink already toasted; keep the dialog open.
    }
  }

  return (
    <DialogShell
      title="Rename programme"
      onClose={onClose}
      busy={loading}
      actionLabel="Save"
      onAction={() => void handleRename()}
    >
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          autoFocus
          maxLength={MAX_NAME_LENGTH}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={`input-field ${error ? 'border-red-300 bg-red-50' : ''}`}
        />
        {error ? (
          <p className="mt-1 text-xs text-red-600">{error}</p>
        ) : (
          <p className="mt-1 text-xs text-gray-400">
            The name is the only rule a published programme still lets you change.
          </p>
        )}
      </div>
    </DialogShell>
  )
}

// ---------------------------------------------------------------------------
// Set end
// ---------------------------------------------------------------------------

/**
 * Moves a published programme's end date. Shortening it (including giving an
 * open-ended programme an end at all) closes every open card on that date, so
 * it needs an explicit confirmation — the same rule the API enforces, checked
 * here too so the operator sees it BEFORE the first refusal. The API stays the
 * authority.
 */
export const SetEndDialog = ({
  program,
  onClose,
}: {
  program: LoyaltyProgram
  onClose: () => void
}) => {
  const currentEnd = parseApiDate(program.ends_at)
  const [endsAt, setEndsAt] = useState(() => (currentEnd ? localDateTimeString(currentEnd) : ''))
  const [confirmed, setConfirmed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // An end at or before now ends the programme outright, which changes which
  // programme is current — so the list is re-read, like after a publish.
  const [setProgramEnd, { loading }] = useMutation(SET_LOYALTY_PROGRAM_END, {
    refetchQueries: [GET_LOYALTY_PROGRAMS],
  })

  const parsed = endsAt ? new Date(endsAt) : null
  const valid = parsed !== null && !Number.isNaN(parsed.getTime())
  // No end yet = open-ended; setting one always shortens the programme. The
  // input holds whole minutes, so an untouched end that carries seconds must
  // not read as "earlier": compare against the current end cut to the minute.
  const currentEndMinute = currentEnd ? Math.floor(currentEnd.getTime() / 60000) * 60000 : null
  const shortening = valid && (currentEndMinute === null || parsed.getTime() < currentEndMinute)

  const handleSave = async () => {
    if (!valid) {
      setError('Pick the moment the programme ends.')
      return
    }
    setError(null)
    try {
      await setProgramEnd({
        variables: {
          id: program.id,
          ends_at: parsed.toISOString(),
          // Only sent when the operator ticked the box, so an accidental
          // shortening is still refused server-side.
          ...(shortening && confirmed ? { confirm_close_cards: true } : {}),
        },
      })
      toast.success('End date set')
      onClose()
    } catch {
      // errorLink already toasted; keep the dialog open.
    }
  }

  return (
    <DialogShell
      title={`End “${program.name}”`}
      onClose={onClose}
      busy={loading}
      actionLabel="Save"
      actionDisabled={shortening && !confirmed}
      onAction={() => void handleSave()}
    >
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Ends at <span className="text-red-500">*</span>
        </label>
        <input
          type="datetime-local"
          value={endsAt}
          onChange={(e) => setEndsAt(e.target.value)}
          className={`input-field ${error ? 'border-red-300 bg-red-50' : ''}`}
        />
        {error ? (
          <p className="mt-1 text-xs text-red-600">{error}</p>
        ) : (
          <p className="mt-1 text-xs text-gray-400">
            {currentEnd
              ? 'The programme currently ends on the date above.'
              : 'The programme is open-ended today.'}
          </p>
        )}
      </div>

      {shortening && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
          <p className="text-xs text-amber-800">
            Shortening closes open cards on that date: unused stamps expire.
          </p>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-amber-900">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="rounded border-amber-300 text-primary-600 focus:ring-primary-500"
            />
            I understand, close the cards
          </label>
        </div>
      )}
    </DialogShell>
  )
}
